import * as animateScrollTo from 'animated-scroll-to';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { alphasCheck, submitAlpha } from '../../../actions/AlphasActions';
import { setAlphaPropertiesData, setAlphaPropertiesError, toggleFunctionsProperty } from '../../../actions/AlphasPropertiesAction';
import { StateType } from '../../../reducers';
import {
  alphasFetchingCheckSelector,
  alphasFetchingPropertiesSelector,
  alphasHasComeFromAlphaPageSelector,
  alphaDetailSelector,
  alphaDetailStatusSelector,
  alphaShowDetailSelector,
  alphaDetailIsTestPeriodControlShown,
  alphaDetailIsTestPeriodShown,
} from '../../../reducers/AlphasReducer/AlphasSelector';
import {
  alphaPropertiesHasErrorsSelector,
  shouldCheckSelector,
  shouldSubmitSelector,
} from '../../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import { getUserPermissionsSelector } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { flashMessagesAlphaDetailsMessageSelector } from '../../../reducers/FlashMessageReducer/FlashMessageSelector';
import { isAlphaCreatedByTheCurrentUserSelector } from '../../../reducers/GenericSelectors';
import { getHeaderHeight } from '../../../shared/helpers';
import { validateFields } from '../../../shared/helpers/AlphaPropertiesValidation';
import { DispatchConnect } from '../../../types';

import { AlphasDetails } from './AlphasDetails';

export interface AlphaDetailsContainerProps {
  handleHideDetail: () => void;
  handleFavorite: (id: string) => void;
  handleVisualization: (id: string) => void;
  openModalCloseProperties: boolean;
  handleNotSaveCloseAlphaProperties: () => void;
  handleCancelCloseAlphaProperties: () => void;
  handlerShowDetail: (alphaDetail: string) => void;
  nextAlphaDetail: string;
}

interface AlphaDetailsContainerStateProps {
  showDetail: any;
  alphaDetail: any;
  flashMessages: any;
  permissions: any;
  alphaStatus: string;
  fetchingCheck: boolean;
  hasComeFromAlphaPage: any;
  hasErrors: boolean;
  fetchingProperties: boolean;
  shouldCheck: boolean;
  shouldSubmit: boolean;
  isAlphaCreatedByTheCurrentUser: boolean;
  isTestPeriodShown: boolean;
  isTestPeriodControlShown: boolean;
  onTestPeriodChange: (isTestPeriodShown: boolean) => void;
}

interface AlphaDetailsContainerDispatchers {
  dispatchSubmitAlpha: (alphaId: string) => void;
  dispatchSimulationCheck: (alphaId: string) => void;
  dispatchSetAlphaPropertiesData: (data: any) => void;
  dispatchSetAlphaPropertiesError: (hasError: boolean) => void;
  dispatchToggleFunctionsProperty: (property: string, value: boolean) => void;
}

type AlphaDetailsContainerGeneric = AlphaDetailsContainerProps & AlphaDetailsContainerStateProps & AlphaDetailsContainerDispatchers;

class AlphaDetailsContainer extends React.Component<AlphaDetailsContainerGeneric> {
  state = {
    windowPageYOffset: 0,
    headerHeight: getHeaderHeight(),
  };

  componentDidMount() {
    window.addEventListener('scroll', this.handleCalculatePageYOffset);
    this.handleCalculatePageYOffset();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleCalculatePageYOffset);
  }

  componentDidUpdate(prevProps: AlphaDetailsContainerGeneric): void {
    if (prevProps.fetchingProperties && !this.props.fetchingProperties && this.props.shouldCheck) {
      this.handleCheckSubmission();
    }

    if (prevProps.fetchingProperties && !this.props.fetchingProperties && this.props.shouldSubmit) {
      this.handleSubmitAlpha();
    }
  }

  handleCalculatePageYOffset = () => {
    const windowPageYOffset = window.pageYOffset <= this.state.headerHeight ? window.pageYOffset : this.state.headerHeight;
    this.setState({
      windowPageYOffset,
    });
  };

  handleScrollToChecks = (elem: string) => {
    const sectionToScroll: any = document.getElementById(`alphas-${elem}`);
    const elemScroll: any = document.getElementById('alphas-details-sections');
    if (sectionToScroll && elemScroll) {
      animateScrollTo(sectionToScroll.offsetTop, { element: elemScroll });
    }
  };

  handleSubmitAlpha = () => {
    if (this.props.fetchingProperties) {
      this.props.dispatchToggleFunctionsProperty('shouldSubmit', true);
    } else {
      this.props.dispatchToggleFunctionsProperty('shouldSubmit', false);
      const hasErrors = validateFields(
        this.props.alphaDetail,
        this.props.dispatchSetAlphaPropertiesData,
        this.props.dispatchSetAlphaPropertiesError
      );

      if (!hasErrors) {
        this.props.dispatchSubmitAlpha(this.props.alphaDetail.get('id'));
        this.handleScrollToChecks('testingStatus');
      } else {
        this.handleScrollToChecks('properties');
      }
    }
  };

  handleCheckSubmission = () => {
    if (this.props.fetchingProperties) {
      this.props.dispatchToggleFunctionsProperty('shouldCheck', true);
    } else {
      this.props.dispatchToggleFunctionsProperty('shouldCheck', false);
      const alphaId = this.props.alphaDetail.get('id');
      const hasErrors = validateFields(
        this.props.alphaDetail,
        this.props.dispatchSetAlphaPropertiesData,
        this.props.dispatchSetAlphaPropertiesError
      );
      if (!hasErrors) {
        this.handleScrollToChecks('testingStatus');
        this.props.dispatchSimulationCheck(alphaId);
      } else {
        this.handleScrollToChecks('properties');
      }
    }
  };

  render() {
    const {
      handleHideDetail,
      showDetail,
      hasComeFromAlphaPage,
      alphaDetail,
      handleFavorite,
      handleVisualization,
      permissions,
      alphaStatus,
      fetchingCheck,
      openModalCloseProperties,
      handleCancelCloseAlphaProperties,
      hasErrors,
      handleNotSaveCloseAlphaProperties,
      isAlphaCreatedByTheCurrentUser,
      flashMessages,
      handlerShowDetail,
      nextAlphaDetail,
      isTestPeriodShown,
      isTestPeriodControlShown,
    } = this.props;

    const { headerHeight } = this.state;

    const activeAlphaInfo = hasComeFromAlphaPage ? null : alphaDetail;
    const hasConsultantPermission = permissions.includes('CONSULTANT') || permissions.includes('RESEARCHER');
    const status = alphaStatus ? alphaStatus : '';

    return (
      <AlphasDetails
        active={showDetail}
        activeAlphaInfo={activeAlphaInfo}
        alphaStatus={status}
        fetchingCheck={fetchingCheck}
        flashMessages={flashMessages}
        handleCancelCloseAlphaProperties={handleCancelCloseAlphaProperties}
        handleCheckSubmission={this.handleCheckSubmission}
        handleFavorite={handleFavorite}
        handleHide={handleHideDetail}
        handleNotSaveCloseAlphaProperties={handleNotSaveCloseAlphaProperties}
        handleSubmitAlpha={this.handleSubmitAlpha}
        handleVisualization={handleVisualization}
        handlerShowDetail={handlerShowDetail}
        hasErrors={hasErrors}
        headerHeight={headerHeight}
        isAlphaCreatedByTheCurrentUser={isAlphaCreatedByTheCurrentUser}
        isConsultant={hasConsultantPermission}
        isTestPeriodControlShown={isTestPeriodControlShown}
        isTestPeriodShown={isTestPeriodShown}
        nextAlphaDetail={nextAlphaDetail}
        openModalCloseProperties={openModalCloseProperties}
      />
    );
  }
}

const mapStateToProps = (state: StateType, ownProps: AlphaDetailsContainerProps): AlphaDetailsContainerStateProps => ({
  showDetail: alphaShowDetailSelector(state),
  hasComeFromAlphaPage: alphasHasComeFromAlphaPageSelector(state),
  alphaDetail: alphaDetailSelector(state),
  permissions: getUserPermissionsSelector(state),
  // @ts-ignore
  alphaStatus: alphaDetailStatusSelector(state),
  // @ts-ignore
  fetchingCheck: alphasFetchingCheckSelector(state),
  flashMessages: flashMessagesAlphaDetailsMessageSelector(state),
  hasErrors: alphaPropertiesHasErrorsSelector(state),
  // @ts-ignore
  fetchingProperties: alphasFetchingPropertiesSelector(state),
  // @ts-ignore
  shouldCheck: shouldCheckSelector(state),
  // @ts-ignore
  shouldSubmit: shouldSubmitSelector(state),
  isAlphaCreatedByTheCurrentUser: isAlphaCreatedByTheCurrentUserSelector(state),
  isTestPeriodShown: alphaDetailIsTestPeriodShown(state),
  isTestPeriodControlShown: alphaDetailIsTestPeriodControlShown(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): AlphaDetailsContainerDispatchers => ({
  dispatchSubmitAlpha: async (alphaId: string) => dispatch(submitAlpha(alphaId)),
  dispatchSimulationCheck: async (alphaId: string) => dispatch(alphasCheck(alphaId)),
  dispatchSetAlphaPropertiesError: (hasError: boolean) => dispatch(setAlphaPropertiesError(hasError)),
  dispatchSetAlphaPropertiesData: (data: any) => dispatch(setAlphaPropertiesData(data)),
  dispatchToggleFunctionsProperty: (property: string, value: boolean) => dispatch(toggleFunctionsProperty(property, value)),
});

export default connect<AlphaDetailsContainerStateProps, DispatchConnect<AlphaDetailsContainerDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(AlphaDetailsContainer);
